// node modules
import React, { Fragment, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, useLocation } from 'react-router-dom';
// services
import BarsProviderServiceWeb from 'modules/TradingChart/components/TradingView/utils/services/barsProviders/BarsProviderLive/BarsProviderLiveWeb';
import SymbolsSearchWebService from 'modules/TradingChart/components/TradingView/utils/services/symbolsSearch/SymbolsSearchWebService';
import HoldingsServiceUpdater from 'modules/TradesPW3/Holdings/HoldingsServiceUpdater';
import PositionsServiceUpdater from 'modules/TradesPW3/Positions/PositionsServiceUpdater';
import HoldingsTvService from 'modules/TradesPW3/Holdings/holdingsTv/HoldingsTvService';
import PlatformService from 'modules/Platform';
import BarsProviderRest from 'modules/TradingChart/components/TradingView/utils/services/barsProviders/BarsProviderRest/BarsProviderRest';
import AnalyticsService from 'modules/Analytics';
import LiveDataService from 'modules/LiveData';
import { SStorage } from 'modules/Storage';
import { useStore } from 'modules/Stores';
// constants
import { EVENTS } from 'modules/Analytics/consts';
// components
import { TradingViewChart } from 'modules/TradingChart';
import UnsupportedBrowserAlert from '../UnsupportedBrowserAlert';

const MainChart = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation<{ search: string, isFromPW3: string }>();

  const isPopup = new URLSearchParams(search).get('isPopup');

  const isFromPW3 = new URLSearchParams(search).get('isFromPW3');

  const { chartInterval, setChartInterval } = useStore('TradingChartStore');

  const { logout } = useStore('AuthStore');

  const { goToRoute, goToExternalRoute } = useStore('RoutingStore');

  const { profile } = useStore('UserStore');

  const { connected: liveV2Reconnected, reconnected: liveV2Connected } = useStore('WsLiveV2Feeder');

  const bypassValidation = JSON.parse(SStorage.getItem('BYPASS_UNSUPPORTED_BROWSER') as string);

  const [isValidBrowser, setIsValidBrowser] = useState(
    bypassValidation ? true : PlatformService.isValidBrowser(),
  );

  const handleSymbolSelect = (newSymbol: string) => {
    const routeOptions = {
      params: {
        id: newSymbol,
      },
      ...(isPopup && {
        query: {
          isPopup: true,
        },
      }),
    };
    goToRoute('/charts/:id', routeOptions);
  };

  const openAddFunds = () => {
    AnalyticsService.sendEvent(EVENTS.ADD_FUNDS, {});
    goToExternalRoute('ADD_FUNDS');
  };

  const openHelp = () => {
    goToExternalRoute('HELP');
  };

  const handleLogout = () => logout(true);

  // Fetch holdings on liveV2 connection
  useEffect(() => {
    if (liveV2Connected || liveV2Reconnected) {
      HoldingsTvService.fetchFormattedHoldings();
    }
  }, [liveV2Connected, liveV2Reconnected]);

  // Resubscribe to watchlist symbols on liveV2 reconnection
  useEffect(() => {
    if (liveV2Reconnected) {
      LiveDataService.resubscribeToWatchlistSymbols();
    }
  }, [liveV2Reconnected]);

  useEffect(() => {
    HoldingsServiceUpdater.registerHoldingUpdater();
    PositionsServiceUpdater.registerPositionsUpdater();
  }, []);

  const onAlertClose = (dontShowMessageAgain: boolean) => {
    setIsValidBrowser(true);
    if (dontShowMessageAgain) {
      SStorage.setItem('BYPASS_UNSUPPORTED_BROWSER', 'true');
    }
  };

  return (
    <Fragment>
      {profile && (
        <Fragment>
          {!isValidBrowser ? (
            <UnsupportedBrowserAlert onClose={onAlertClose} />
          ) : (
            <TradingViewChart
              onIntervalChanged={setChartInterval}
              interval={chartInterval}
              handleSymbolSelect={handleSymbolSelect}
              handleAddFunds={openAddFunds}
              symbol={id}
              isFromPW3={isFromPW3 === 'true'}
              symbolsSearchService={SymbolsSearchWebService}
              barsProviderService={BarsProviderServiceWeb}
              barsProviderRest={BarsProviderRest}
              handleLogout={handleLogout}
              handleHelp={openHelp}
              isPopup={isPopup}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default observer(MainChart);
